import {ReactNode} from 'react';

import {OrderStatus, OrderTypes} from 'core/dictionaries';

import {PackageTypes} from './gtins';

export enum OrdersAccessors {
    participant = 'participant',
    id = 'orderId',
    createdDate = 'createdAt',
    closedDate = 'closedAt',
    quantity = 'totalMarkingCodes',
    packagingType = 'packagingType',
    status = 'status',
    orderType = 'orderType',
}

export interface OrdersGridRawRow {
    participant?: string;
    orderId: string;
    createdAt: string;
    closedAt: string;
    totalMarkingCodes: number;
    packagingType: PackageTypes;
    status: OrderStatus;
    orderType: OrderTypes;
}

export interface OrdersGridFormattedRow {
    [OrdersAccessors.participant]?: string;
    [OrdersAccessors.id]: ReactNode;
    [OrdersAccessors.createdDate]: string;
    [OrdersAccessors.closedDate]: string;
    [OrdersAccessors.quantity]: number;
    [OrdersAccessors.packagingType]: PackageTypes;
    [OrdersAccessors.status]: ReactNode;
    [OrdersAccessors.orderType]: OrderTypes;
}

export interface OrderFormData {
    country?: {
        value: string;
        label: string;
    } | null;
    factoryId?: {
        value: string;
        label: string;
    } | null;
    factoryName?: string;
    productionLineId?: {
        value: string;
        label: string;
    } | null;
    productionLineName?: string;
    productGroupCode?: string;
    type?: keyof typeof PackageTypes;
    productTypes?: 'local' | 'import' | 'both';
    manufName?: string;
    manufAddress?: string;
}

export interface OrderCreatePayload {
    country?: string;
    factoryId?: string;
    factoryName?: string;
    productionLineId?: string;
    productionLineName?: string;
    productGroupCode?: string;
    type?: keyof typeof PackageTypes;
    productTypes?: 'local' | 'import' | 'both';
    manufName?: string;
    manufAddress?: string;
}

export interface OrderProductsData {
    type?: keyof typeof PackageTypes;
    gtin?: string;
    quantity?: string;
    templateId?: string;
    productName?: string;
    productGroupCode?: string;
    productVersion?: number;
    productVersionId?: string;
    manufacturingDate?: string;
    expirationDate?: string;
    batchNumber?: string;
}

export interface OrderCreateData extends OrderCreatePayload {
    products: Array<OrderProductsData>;
}

export enum OrdersDetailProductsAssessors {
    templateId = 'templateId',
    gtin = 'gtin',
    productId = 'productId',
    productName = 'productName',
    productVendorCode = 'productVendorCode',
    productGroupCode = 'productGroupCode',
    quantity = 'quantity',
    availableQuantity = 'availableQuantity',
    type = 'type',
    packageType = 'packageType',
    printed = 'printed',
}

export interface OrderDetailProductsRaw {
    templateId: string;
    gtin: string;
    productId: string;
    productName: string;
    productVendorCode: string;
    productGroupCode: string;
    quantity: number;
    availableQuantity: number;
    packageType: PackageTypes;
    printed?: boolean;
}

export interface OrderDetailProductsFormattedRow {
    productId: string;
    productName: string;
    quantity: number;
    availableQuantity: string;
    gtin: string;
    packageType: PackageTypes;
    printed?: boolean;
}

export interface OrderDetailRaw {
    orderId: string;
    createdAt: string;
    status: OrderStatus;
    totalMarkingCodes: number;
    manufacturingSite: string;
    country: string;
    manufacturingLine: string;
    productGroupCode: string;
    products: Array<OrderDetailProductsRaw>;
    errors?: OrderDetailError[];
    availableCodes: number;
    manufacturerName?: string;
    manufacturerAddress?: string;
    orderType: OrderTypes;
}

export interface OrderDetailError {
    code: string;
    message: string;
}

export interface OrderDetailFormatted {
    orderId: string;
    createdAt: string;
    status: string;
    totalMarkingCodes: string;
    manufacturingSite: string;
    country: string;
    manufacturingLine: string;
    availableCodes: string;
    manufacturerName?: string;
    manufacturerAddress?: string;
    orderType: OrderTypes;
    errors?: string[];
}

export const printExtensions = ['PDF', 'EPS', 'CSV', 'XLS'] as const;
type PrintExtension = typeof printExtensions[number];
export const printFormats = [
    'A4',
    'LETTER',
    'STICKER_SMALL_H',
    'STICKER_SMALL_V',
    'STICKER_LARGE_H',
    'STICKER_LARGE_V',
] as const;
type PageFormat = typeof printFormats[number];

export interface FormattedLabelsForm {
    format: PrintExtension;
    pageFormat: PageFormat;
    orderId: string;
    gtin: string;
    showHri: boolean;
}
